import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Skeleton,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { AddOutlined, DeleteOutlined, EditOutlined, SearchOutlined, VisibilityOffOutlined, VisibilityOutlined } from "@mui/icons-material";
import { AdminVendorConnection, AdminConnectionsMatch } from "types/interfaces/adminUsers";
import CustomScrollbars from "../CustomScrollbars";
import {
  useGetAdminUserIntegrationsQuery,
  useGetAvailableIntegrationsQuery,
  useGrantIntegrationAccessMutation,
  useRevokeIntegrationAccessMutation,
  useUpdateAdminConnectionMutation,
  useToggleIntegrationEnabledMutation,
} from "features/adminUsers/adminUsers";

interface Props {
  userId: number;
  canEdit?: boolean;
  onSuccess?: (message: string, severity: "success" | "error") => void;
}

type FieldKey = "host" | "port" | "username" | "password" | "passphrase" | "key" | "client_code" | "access_token";

const FIELD_LABELS: Record<FieldKey, string> = {
  host: "Host",
  port: "Port",
  username: "Username",
  password: "Password",
  passphrase: "Passphrase",
  key: "Private Key",
  client_code: "Client Code",
  access_token: "Access Token",
};

const SENSITIVE_FIELDS = new Set<FieldKey>(["password", "passphrase", "key", "access_token"]);

/** All configurable field keys — any can be added/edited/deleted for any connection type. */
const ALL_FIELD_KEYS: FieldKey[] = ["host", "port", "username", "password", "passphrase", "key", "client_code", "access_token"];

function FieldRow({
  fieldKey,
  value,
  onEdit,
  onClear,
}: {
  fieldKey: FieldKey;
  value: string;
  onEdit: () => void;
  onClear?: () => void;
}) {
  const isSensitive = SENSITIVE_FIELDS.has(fieldKey);
  const displayValue = isSensitive
    ? "••••••••"
    : value.length > 40 ? value.slice(0, 40) + "…" : value;

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      py={0.75}
      px={1.5}
      sx={{ "&:hover": { bgcolor: "rgba(0,0,0,0.02)" } }}
    >
      <Box flex={1} minWidth={0}>
        <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.3}>
          {FIELD_LABELS[fieldKey]}
        </Typography>
        <Typography variant="body2" noWrap sx={{ fontFamily: isSensitive ? "monospace" : "inherit" }}>
          {displayValue}
        </Typography>
      </Box>
      <Box display="flex" alignItems="center" gap={0.25} flexShrink={0}>
        {onClear && (
          <Tooltip title="Clear field">
            <IconButton size="small" onClick={onClear} aria-label={`Clear ${FIELD_LABELS[fieldKey]}`}>
              <DeleteOutlined sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
        <IconButton size="small" onClick={onEdit} aria-label={`Edit ${FIELD_LABELS[fieldKey]}`}>
          <EditOutlined sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>
    </Box>
  );
}

function FieldEditDialog({
  fieldKey,
  currentValue,
  connId,
  onClose,
  onSuccess,
}: {
  fieldKey: FieldKey;
  currentValue: string;
  connId: number;
  onClose: () => void;
  onSuccess?: (message: string, severity: "success" | "error") => void;
}) {
  const [value, setValue] = useState(currentValue);
  const [showValue, setShowValue] = useState(false);
  const isSensitive = SENSITIVE_FIELDS.has(fieldKey);
  const isKey = fieldKey === "key";
  const [updateConnection, { isLoading }] = useUpdateAdminConnectionMutation();

  const handleSave = async () => {
    try {
      await updateConnection({ connId, [fieldKey]: value } as any).unwrap();
      onSuccess?.("Connection updated.", "success");
      onClose();
    } catch {
      onSuccess?.("Failed to update connection.", "error");
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>Edit {FIELD_LABELS[fieldKey]}</DialogTitle>
      <DialogContent>
        <TextField
          value={value}
          onChange={(e) => setValue(e.target.value)}
          fullWidth
          size="small"
          multiline={isKey}
          rows={isKey ? 4 : undefined}
          sx={{ mt: 0.5, "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontFamily: isSensitive ? "monospace" : "inherit", fontSize: 13 } }}
          inputProps={{
            autoComplete: "new-password",
            style: isSensitive && !isKey ? { WebkitTextSecurity: showValue ? "none" : "disc" } as React.CSSProperties : undefined,
          }}
          InputProps={isSensitive && !isKey ? {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setShowValue(!showValue)} edge="end">
                  {showValue ? <VisibilityOutlined sx={{ fontSize: 16 }} /> : <VisibilityOffOutlined sx={{ fontSize: 16 }} />}
                </IconButton>
              </InputAdornment>
            ),
          } : undefined}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isLoading || value === currentValue}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          {isLoading ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AddFieldRow({
  fieldKey,
  connId,
  onSuccess,
}: {
  fieldKey: FieldKey;
  connId: number;
  onSuccess?: (message: string, severity: "success" | "error") => void;
}) {
  const [value, setValue] = useState("");
  const [showValue, setShowValue] = useState(false);
  const isSensitive = SENSITIVE_FIELDS.has(fieldKey);
  const isKey = fieldKey === "key";
  const [updateConnection, { isLoading }] = useUpdateAdminConnectionMutation();

  const handleAdd = async () => {
    if (!value.trim()) return;
    try {
      await updateConnection({ connId, [fieldKey]: value } as any).unwrap();
      onSuccess?.("Connection updated.", "success");
      setValue("");
    } catch {
      onSuccess?.("Failed to update connection.", "error");
    }
  };

  return (
    <Box display="flex" gap={1} alignItems="flex-start" mt={1}>
      <TextField
        label={FIELD_LABELS[fieldKey]}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        size="small"
        fullWidth
        multiline={isKey}
        rows={isKey ? 3 : undefined}
        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 13 } }}
        inputProps={{
          autoComplete: "new-password",
          style: isSensitive && !isKey ? { WebkitTextSecurity: showValue ? "none" : "disc" } as React.CSSProperties : undefined,
        }}
        InputProps={isSensitive && !isKey ? {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setShowValue(!showValue)} edge="end">
                {showValue ? <VisibilityOutlined sx={{ fontSize: 16 }} /> : <VisibilityOffOutlined sx={{ fontSize: 16 }} />}
              </IconButton>
            </InputAdornment>
          ),
        } : undefined}
      />
      <Button
        variant="outlined"
        size="small"
        onClick={handleAdd}
        disabled={isLoading || !value.trim()}
        sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5, flexShrink: 0, mt: 0.25 }}
      >
        Add
      </Button>
    </Box>
  );
}

function ConnectionEditSection({
  conn,
  onSuccess,
}: {
  conn: AdminVendorConnection;
  onSuccess?: (message: string, severity: "success" | "error") => void;
}) {
  const [editField, setEditField] = useState<FieldKey | null>(null);
  const [addFieldsOpen, setAddFieldsOpen] = useState(false);
  const [updateConnection] = useUpdateAdminConnectionMutation();

  const filledFields = ALL_FIELD_KEYS.filter((f) => {
    const val = conn[f as keyof AdminVendorConnection];
    return val != null && val !== "";
  });
  const emptyFields = ALL_FIELD_KEYS.filter((f) => {
    const val = conn[f as keyof AdminVendorConnection];
    return val == null || val === "";
  });

  const handleClearField = (fieldKey: FieldKey) => {
    updateConnection({ connId: conn.id, [fieldKey]: "" })
      .unwrap()
      .then(() => onSuccess?.("Connection updated.", "success"))
      .catch(() => onSuccess?.("Failed to update connection.", "error"));
  };

  const typeColors: Record<string, { bg: string; color: string }> = {
    ftp: { bg: "rgba(21,101,192,0.10)", color: "#1565c0" },
    bot: { bg: "rgba(106,27,154,0.10)", color: "#6a1b9a" },
    api: { bg: "rgba(0,121,107,0.10)", color: "#00796b" },
  };
  const chipStyle = typeColors[conn.type] ?? { bg: "rgba(0,0,0,0.07)", color: "text.secondary" };

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={1.5}>
        <Typography variant="body2" fontWeight={700}>{conn.vendor}</Typography>
        <Chip
          label={conn.type.toUpperCase()}
          size="small"
          sx={{ height: 16, fontSize: 10, fontWeight: 700, bgcolor: chipStyle.bg, color: chipStyle.color }}
        />
      </Box>

      {filledFields.length > 0 && (
        <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, overflow: "hidden" }}>
          {filledFields.map((field, i) => (
            <Box key={field} sx={{ borderBottom: i < filledFields.length - 1 ? "1px solid" : "none", borderColor: "divider" }}>
              <FieldRow
                fieldKey={field}
                value={String(conn[field as keyof AdminVendorConnection] ?? "")}
                onEdit={() => setEditField(field)}
                onClear={() => handleClearField(field)}
              />
            </Box>
          ))}
        </Box>
      )}

      <Box mt={1.5}>
        <Button
          size="small"
          onClick={() => setAddFieldsOpen(!addFieldsOpen)}
          sx={{ textTransform: "none", fontSize: 12, color: "text.secondary", p: 0 }}
        >
          {addFieldsOpen ? "− Hide fields" : emptyFields.length > 0 ? `+ Add fields (${emptyFields.length})` : "+ Add fields"}
        </Button>
        {addFieldsOpen && emptyFields.length > 0 && (
          <Box mt={0.5}>
            {emptyFields.map((field) => (
              <AddFieldRow key={field} fieldKey={field} connId={conn.id} onSuccess={onSuccess} />
            ))}
          </Box>
        )}
      </Box>

      {editField && (
        <FieldEditDialog
          fieldKey={editField}
          currentValue={String(conn[editField as keyof AdminVendorConnection] ?? "")}
          connId={conn.id}
          onClose={() => setEditField(null)}
          onSuccess={onSuccess}
        />
      )}
    </Box>
  );
}

function IntegrationDetailsDialog({
  match,
  onClose,
  onSuccess,
}: {
  match: AdminConnectionsMatch | null;
  onClose: () => void;
  onSuccess?: (message: string, severity: "success" | "error") => void;
}) {
  return (
    <Dialog open={!!match} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>{match?.integration_name}</DialogTitle>
      <DialogContent dividers>
        {match && (
          <>
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              textTransform="uppercase"
              letterSpacing={0.6}
              display="block"
              mb={1.5}
            >
              Source
            </Typography>
            <ConnectionEditSection conn={match.source_connection} onSuccess={onSuccess} />

            <Divider sx={{ my: 2.5 }} />

            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              textTransform="uppercase"
              letterSpacing={0.6}
              display="block"
              mb={1.5}
            >
              Destination
            </Typography>
            <ConnectionEditSection conn={match.destination_connection} onSuccess={onSuccess} />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const METHOD_TYPE_COLORS: Record<string, { bg: string; color: string }> = {
  Webhook: { bg: "rgba(21,101,192,0.10)", color: "#1565c0" },
  Scheduler: { bg: "rgba(46,125,50,0.10)", color: "#2e7d32" },
  "S3-triggered": { bg: "rgba(230,81,0,0.10)", color: "#e65100" },
};

function parseIntegrationName(name: string): { methodType: string | null; displayName: string } {
  const match = name.match(/^\[([^\]]+)\]\s*/);
  if (match) {
    return { methodType: match[1], displayName: name.slice(match[0].length) };
  }
  return { methodType: null, displayName: name };
}

function MatchRow({
  match,
  userId,
  onEdit,
  canEdit,
}: {
  match: AdminConnectionsMatch;
  userId: number;
  onEdit: (match: AdminConnectionsMatch) => void;
  canEdit: boolean;
}) {
  const [enabled, setEnabled] = useState(match.enabled);
  useEffect(() => { setEnabled(match.enabled); }, [match.enabled]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [toggle, { isLoading }] = useToggleIntegrationEnabledMutation();
  const [revokeAccess, { isLoading: isRevoking }] = useRevokeIntegrationAccessMutation();

  const handleToggleClick = () => canEdit && setConfirmOpen(true);

  const handleConfirm = async () => {
    const prev = enabled;
    setConfirmOpen(false);
    setEnabled(!prev);
    try {
      await toggle({ matchId: match.id, userId }).unwrap();
    } catch {
      setEnabled(prev);
    }
  };

  const { methodType, displayName } = parseIntegrationName(match.integration_name);
  const methodColors = methodType ? (METHOD_TYPE_COLORS[methodType] ?? { bg: "rgba(0,0,0,0.07)", color: "text.secondary" }) : null;

  const handleRevoke = async () => {
    await revokeAccess({ userId, matchId: match.id });
    setRevokeOpen(false);
  };

  return (
    <>
      <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, p: 1.5, mb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={0.75} flex={1} minWidth={0}>
            {methodColors && (
              <Chip
                label={methodType}
                size="small"
                sx={{ height: 16, fontSize: 10, fontWeight: 700, bgcolor: methodColors.bg, color: methodColors.color, flexShrink: 0 }}
              />
            )}
            {match.is_shared && (
              <Chip
                label={match.owner_username ? `Shared from ${match.owner_username}` : "Shared"}
                size="small"
                sx={{ height: 16, fontSize: 10, fontWeight: 700, bgcolor: "rgba(0,0,0,0.06)", color: "text.secondary", flexShrink: 0, maxWidth: 160 }}
              />
            )}
            <Typography variant="body2" fontWeight={700} noWrap sx={{ minWidth: 0 }}>
              {displayName}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5} flexShrink={0}>
            <Tooltip title={!canEdit ? "Integration Manager or Super Admin required to edit or share integrations." : ""}>
              <span>
                {match.is_shared ? (
                  <IconButton size="small" onClick={() => canEdit && setRevokeOpen(true)} color="error" disabled={!canEdit}>
                    <DeleteOutlined sx={{ fontSize: 16 }} />
                  </IconButton>
                ) : (
                  <IconButton size="small" onClick={() => canEdit && onEdit(match)} disabled={!canEdit}>
                    <EditOutlined sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </span>
            </Tooltip>
            <Tooltip title={!canEdit ? "Integration Manager or Super Admin required to edit or share integrations." : ""}>
              <span>
                <Switch size="small" checked={enabled} onChange={handleToggleClick} disabled={!canEdit || isLoading} />
              </span>
            </Tooltip>
          </Box>
        </Box>
        <Typography variant="caption" color="text.secondary">
          {match.source_connection.vendor} → {match.destination_connection.vendor}
        </Typography>
        {match.frequency && (
          <Typography variant="caption" color="text.secondary" display="block">
            {match.frequency}{match.last_upload ? ` · Last run: ${match.last_upload}` : ""}
          </Typography>
        )}
      </Box>

      <Dialog open={revokeOpen} onClose={() => setRevokeOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>Remove Access?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Remove shared access to <strong>{displayName}</strong> for this user?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRevokeOpen(false)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleRevoke} disabled={isRevoking} sx={{ textTransform: "none", fontWeight: 700 }}>
            {isRevoking ? "Removing..." : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
          {enabled ? "Disable Integration?" : "Enable Integration?"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {enabled
              ? "This will stop the integration from running."
              : "This will allow the integration to run."
            }
          </Typography>
          <Typography variant="body2" fontWeight={600} mt={1}>
            {displayName}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            color={enabled ? "error" : "primary"}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {enabled ? "Disable" : "Enable"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default function UserManagementIntegrationsTab({ userId, canEdit = true, onSuccess }: Props) {
  const { data, isLoading } = useGetAdminUserIntegrationsQuery(userId);
  const [selectedMatchId, setSelectedMatchId] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [addSearch, setAddSearch] = useState("");
  const [grantError, setGrantError] = useState(false);
  const { data: availableData, isLoading: isLoadingAvailable } = useGetAvailableIntegrationsQuery(userId, { skip: !addOpen });
  const [grantAccess] = useGrantIntegrationAccessMutation();

  if (isLoading) {
    return (
      <Box px={2.5} pt={2}>
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} variant="rounded" height={64} sx={{ mb: 1, borderRadius: 1.5 }} />
        ))}
      </Box>
    );
  }

  const matches = data?.matches ?? [];
  const selectedMatch = selectedMatchId != null ? matches.find((m) => m.id === selectedMatchId) ?? null : null;
  const filteredMatches = search.trim()
    ? matches.filter((m) =>
        m.integration_name.toLowerCase().includes(search.toLowerCase()) ||
        m.source_connection.vendor.toLowerCase().includes(search.toLowerCase()) ||
        m.destination_connection.vendor.toLowerCase().includes(search.toLowerCase())
      )
    : matches;

  return (
    <>
      {matches.length > 0 && (
        <Box px={2} pt={1.5} pb={1}>
          <TextField
            size="small"
            placeholder="Search integrations..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined sx={{ fontSize: 16, color: "text.disabled" }} />
                </InputAdornment>
              ),
            }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 13 } }}
          />
        </Box>
      )}

      <CustomScrollbars sx={{ flex: 1, minHeight: 0, px: 2, pb: 1 }}>
        {matches.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" mt={3}>
            No integrations found for this user.
          </Typography>
        ) : filteredMatches.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" mt={2}>
            No integrations match your search.
          </Typography>
        ) : filteredMatches.map((match) => (
          <MatchRow key={match.id} match={match} userId={userId} onEdit={(m) => setSelectedMatchId(m.id)} canEdit={canEdit} />
        ))}
      </CustomScrollbars>

      <Box px={2} pb={2} pt={1} sx={!canEdit ? { opacity: 0.75 } : undefined}>
        <Tooltip title={!canEdit ? "Integration Manager or Super Admin required to edit or share integrations." : ""}>
          <span>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<AddOutlined />}
              onClick={() => { if (canEdit) { setAddOpen(true); setAddSearch(""); } }}
              disabled={!canEdit}
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5 }}
            >
              Share Integration
            </Button>
          </span>
        </Tooltip>
      </Box>

      <Dialog open={addOpen} onClose={() => { setAddOpen(false); setGrantError(false); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>Share Integration</DialogTitle>
        <DialogContent>
          <TextField
            size="small"
            placeholder="Search integrations..."
            value={addSearch}
            onChange={(e) => setAddSearch(e.target.value)}
            fullWidth
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined sx={{ fontSize: 16, color: "text.disabled" }} />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 13 } }}
          />
          {grantError && (
            <Typography variant="caption" color="error" display="block" mb={1}>
              Failed to share integration. Please try again.
            </Typography>
          )}
          {isLoadingAvailable ? (
            <Box pt={1}>{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} variant="text" height={40} />)}</Box>
          ) : (availableData?.integrations.length ?? 0) === 0 ? (
            <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
              No integrations available to add.
            </Typography>
          ) : (
            <CustomScrollbars sx={{ maxHeight: 320 }}>
              {availableData!.integrations
                .filter((i) =>
                  !addSearch.trim() ||
                  i.integration_name.toLowerCase().includes(addSearch.toLowerCase()) ||
                  i.source_vendor.toLowerCase().includes(addSearch.toLowerCase()) ||
                  i.destination_vendor.toLowerCase().includes(addSearch.toLowerCase())
                )
                .map((integration) => (
                  <Box
                    key={integration.id}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    py={0.75}
                    px={1}
                    sx={{ borderRadius: 1, cursor: "pointer", "&:hover": { bgcolor: "rgba(0,0,0,0.03)" } }}
                  >
                    <Box minWidth={0}>
                      <Typography variant="body2" fontWeight={700} noWrap>{integration.integration_name}</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {integration.source_vendor} → {integration.destination_vendor}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={async () => {
                        try {
                          setGrantError(false);
                          await grantAccess({ userId, matchId: integration.id }).unwrap();
                        } catch {
                          setGrantError(true);
                        }
                      }}
                      sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5, flexShrink: 0, ml: 1 }}
                    >
                      Add
                    </Button>
                  </Box>
                ))}
            </CustomScrollbars>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddOpen(false)} sx={{ textTransform: "none" }}>Done</Button>
        </DialogActions>
      </Dialog>

      <IntegrationDetailsDialog match={selectedMatch} onClose={() => setSelectedMatchId(null)} onSuccess={onSuccess} />
    </>
  );
}
